import { useState } from 'react';
import { Plus, Trash2, ChevronUp, ChevronDown, Save, Eye, Rocket, Undo2, History, RotateCcw } from 'lucide-react';
import { useAdminContent } from '@/admin/AdminContentContext';
import { FieldRow, Toggle } from '@/admin/fields';
import { getAt } from '@/lib/content';

const tid = (p) => p.replace(/\./g, '-');
const fmt = (d) => (d ? new Date(d).toLocaleString('ar-IQ', { dateStyle: 'medium', timeStyle: 'short' }) : '—');

function Card({ title, right, children, testid }) {
  return (
    <section className="rounded-3xl border border-brand-line bg-white p-6 shadow-[0_20px_50px_-40px_rgba(15,23,42,0.35)]" data-testid={testid}>
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <h3 className="font-display text-base font-extrabold text-brand-ink">{title}</h3>
        {right}
      </div>
      {children}
    </section>
  );
}

function VisibilityToggle({ path }) {
  const { get, setShared } = useAdminContent();
  const v = get('ar', path);
  return <Toggle checked={v !== false} onChange={(val) => setShared(path, val)} testid={`visible-${tid(path)}`} />;
}

function ListEditor({ list }) {
  const { draft, addItem, removeItem, moveItem, setShared } = useAdminContent();
  const items = getAt(draft.ar, list.path) || [];
  const [openIdx, setOpenIdx] = useState(null);
  return (
    <div className="space-y-3" data-testid={`list-${tid(list.path)}`}>
      {items.map((it, i) => {
        const titleField = list.fields.find((f) => !['shared', 'link', 'image', 'toggle', 'date', 'number', 'lines'].includes(f.type)) || list.fields[0];
        const title = getAt(draft.ar, `${list.path}.${i}.${titleField.path}`) || getAt(draft.en, `${list.path}.${i}.${titleField.path}`) || `${list.label} ${i + 1}`;
        const open = openIdx === i;
        return (
          <div key={it.id || i} className={`rounded-2xl border transition-colors ${open ? 'border-brand-blue/40 bg-brand-blue/[0.02]' : 'border-brand-line bg-brand-paper/60'}`} data-testid={`list-item-${tid(list.path)}-${i}`}>
            <div className="flex flex-wrap items-center gap-2 px-4 py-3">
              <button type="button" onClick={() => setOpenIdx(open ? null : i)} data-testid={`list-item-toggle-${tid(list.path)}-${i}`} className="flex-1 truncate text-start text-sm font-bold text-brand-ink">
                <span className="me-2 font-mono text-[10px] text-brand-dim">{String(i + 1).padStart(2, '0')}</span>{typeof title === 'string' ? title : `${list.label} ${i + 1}`}
              </button>
              <Toggle checked={it.visible !== false} onChange={(v) => setShared(`${list.path}.${i}.visible`, v)} testid={`list-item-visible-${tid(list.path)}-${i}`} />
              <div className="flex items-center gap-1">
                <button type="button" onClick={() => moveItem(list.path, i, -1)} disabled={i === 0} data-testid={`list-item-up-${tid(list.path)}-${i}`} className="grid h-8 w-8 place-items-center rounded-lg text-brand-steel hover:bg-white disabled:opacity-30"><ChevronUp className="h-4 w-4" /></button>
                <button type="button" onClick={() => moveItem(list.path, i, 1)} disabled={i === items.length - 1} data-testid={`list-item-down-${tid(list.path)}-${i}`} className="grid h-8 w-8 place-items-center rounded-lg text-brand-steel hover:bg-white disabled:opacity-30"><ChevronDown className="h-4 w-4" /></button>
                <button type="button" onClick={() => { if (window.confirm('حذف هذا العنصر من جميع اللغات؟')) removeItem(list.path, i); }} data-testid={`list-item-delete-${tid(list.path)}-${i}`} className="grid h-8 w-8 place-items-center rounded-lg text-brand-dim hover:bg-brand-crimson/10 hover:text-brand-crimson"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
            {open && (
              <div className="space-y-4 border-t border-brand-line/70 px-4 py-4">
                {list.fields.map((f) => <FieldRow key={f.path} field={f} base={`${list.path}.${i}`} />)}
              </div>
            )}
          </div>
        );
      })}
      <button type="button" onClick={() => { addItem(list.path); setOpenIdx(items.length); }} data-testid={`list-add-${tid(list.path)}`}
        className="inline-flex items-center gap-2 rounded-full border border-dashed border-brand-blue/40 px-4 py-2 text-xs font-bold text-brand-blue hover:bg-brand-blue/5">
        <Plus className="h-3.5 w-3.5" /> إضافة {list.label}
      </button>
    </div>
  );
}

export function Toolbar({ title }) {
  const { dirty, busy, meta, save, publish, discard, openPreview, user } = useAdminContent();
  const isAdmin = user?.role === 'admin';
  const hasUnpublished = meta.updated_at && (!meta.published_at || meta.updated_at > meta.published_at);
  return (
    <div className="sticky top-0 z-30 -mx-1 mb-6 flex flex-wrap items-center gap-3 rounded-2xl border border-brand-line bg-white/90 px-4 py-3 backdrop-blur" data-testid="editor-toolbar">
      <div className="me-auto">
        <h2 className="font-display text-lg font-extrabold text-brand-ink">{title}</h2>
        <p className="font-mono text-[10px] text-brand-dim" data-testid="editor-meta">
          آخر حفظ: {fmt(meta.updated_at)} {meta.updated_by ? `· ${meta.updated_by}` : ''} — آخر نشر: {fmt(meta.published_at)}
          {dirty && <span className="ms-2 rounded-full bg-brand-amber/15 px-2 py-0.5 text-amber-700" data-testid="dirty-badge">تغييرات غير محفوظة</span>}
          {!dirty && hasUnpublished && <span className="ms-2 rounded-full bg-brand-sky/15 px-2 py-0.5 text-sky-700" data-testid="unpublished-badge">مسودة غير منشورة</span>}
        </p>
      </div>
      <button type="button" onClick={discard} disabled={busy} data-testid="editor-discard-btn" className="inline-flex items-center gap-1.5 rounded-full border border-brand-line px-4 py-2 text-xs font-bold text-brand-steel hover:text-brand-crimson disabled:opacity-50"><Undo2 className="h-3.5 w-3.5" /> تجاهل المسودة</button>
      <button type="button" onClick={openPreview} disabled={busy} data-testid="editor-preview-btn" className="inline-flex items-center gap-1.5 rounded-full border border-brand-line px-4 py-2 text-xs font-bold text-brand-ink hover:border-brand-blue hover:text-brand-blue disabled:opacity-50"><Eye className="h-3.5 w-3.5" /> معاينة</button>
      <button type="button" onClick={() => save()} disabled={busy || !dirty} data-testid="editor-save-btn" className="inline-flex items-center gap-1.5 rounded-full bg-brand-ink px-4 py-2 text-xs font-bold text-white hover:opacity-90 disabled:opacity-40"><Save className="h-3.5 w-3.5" /> حفظ كمسودة</button>
      {isAdmin && <button type="button" onClick={publish} disabled={busy} data-testid="editor-publish-btn" className="inline-flex items-center gap-1.5 rounded-full bg-brand-blue px-4 py-2 text-xs font-bold text-white hover:bg-brand-blueHover disabled:opacity-50"><Rocket className="h-3.5 w-3.5" /> نشر</button>}
      {!isAdmin && <span className="rounded-full bg-brand-alt px-3 py-1.5 text-[10px] font-bold text-brand-dim" data-testid="editor-role-note">النشر متاح لمدير النظام فقط</span>}
    </div>
  );
}

export function RevisionsPanel() {
  const { revisions, restore, busy, resetDefaults, user } = useAdminContent();
  const isAdmin = user?.role === 'admin';
  return (
    <Card title="النسخ السابقة" testid="revisions-panel" right={isAdmin && (
      <button type="button" onClick={() => { if (window.confirm('إعادة المسودة إلى المحتوى الافتراضي؟')) resetDefaults(); }} data-testid="reset-defaults-btn" className="inline-flex items-center gap-1.5 rounded-full border border-brand-line px-3 py-1.5 text-[11px] font-bold text-brand-steel hover:text-brand-crimson"><RotateCcw className="h-3.5 w-3.5" /> المحتوى الافتراضي</button>
    )}>
      {revisions.length === 0 ? <p className="text-sm text-brand-dim" data-testid="revisions-empty">لا توجد نسخ سابقة بعد — تُحفظ نسخة تلقائياً عند كل عملية نشر.</p> : (
        <ul className="divide-y divide-brand-line">
          {revisions.map((r, i) => (
            <li key={r.id} className="flex flex-wrap items-center gap-3 py-3" data-testid={`revision-${i}`}>
              <History className="h-4 w-4 text-brand-blue" />
              <span className="text-sm font-bold text-brand-ink">{fmt(r.at)}</span>
              <span className="font-mono text-[10px] text-brand-dim" dir="ltr">{r.by}</span>
              {isAdmin && <button type="button" disabled={busy} onClick={() => { if (window.confirm('استرجاع هذه النسخة إلى المسودة؟')) restore(r.id); }} data-testid={`revision-restore-${i}`} className="ms-auto rounded-full border border-brand-blue/30 px-3 py-1.5 text-[11px] font-bold text-brand-blue hover:bg-brand-blue/5">استرجاع</button>}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

export default function ContentEditor({ section }) {
  const { draft } = useAdminContent();
  if (!draft) return <p className="py-20 text-center text-sm text-brand-dim" data-testid="editor-loading">جارٍ تحميل المحتوى…</p>;
  return (
    <div data-testid={`editor-${section.id}`}>
      <Toolbar title={section.label} />
      <div className="space-y-5">
        {section.groups.map((g, gi) => (
          <Card key={gi} title={g.title} testid={`group-${section.id}-${gi}`} right={g.visibleKey && <VisibilityToggle path={g.visibleKey} />}>
            {g.fields && <div className="space-y-4">{g.fields.map((f) => <FieldRow key={f.path} field={f} />)}</div>}
            {g.list && <ListEditor list={g.list} />}
          </Card>
        ))}
        {section.id === 'settings' && <RevisionsPanel />}
      </div>
    </div>
  );
}
